// Ubicación en el repo: src/components/AudiogramSVG.jsx
// Audiograma — mismo layout y símbolos que drawAudiogramSVG() del
// prototipo (línea ~1987): vía aérea (○ OD / × OI, conectada con línea
// sólida), vía ósea (< OD / > OI, conectada con línea DISCONTINUA — bug
// corregido 22-sep-2026, antes no se conectaba en absoluto, ver
// DECISIONS.md), LDL (▷ OD / ◁ OI, sin conectar).
//
// Enmascaramiento por frecuencia (migración 0053, 16-sep-2026, pedido de
// Nicolás): símbolos clínicos reconocidos, no inventados — vía aérea
// enmascarada cambia a △ abierto (OD) / □ abierto (OI) en vez de ○/×; vía
// ósea enmascarada cambia a "[" (OD) / "]" (OI) en vez de </>. Cada punto
// de la serie elige su símbolo individualmente según si ESA frecuencia
// puntual está marcada como enmascarada — por eso `symbolFor` es una
// función (frecuencia → símbolo), no un símbolo fijo por serie como antes.
import { FREQS } from '../lib/exams';

const W = 560;
const H = 350;
const PAD_L = 58;
const PAD_R = 22;
const PAD_T = 18;
const PAD_B = 38;
const PLOT_W = W - PAD_L - PAD_R;
const PLOT_H = H - PAD_T - PAD_B;
const DB_LINES = [-10, 0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120];
const MONO = "'JetBrains Mono', monospace";

function xFor(freq) {
  return PAD_L + (FREQS.indexOf(freq) / (FREQS.length - 1)) * PLOT_W;
}
function yFor(db) {
  return PAD_T + ((db + 10) / 130) * PLOT_H;
}

function Marker({ x, y, color, symbol }) {
  if (symbol === 'circle') return <circle cx={x} cy={y} r={6} fill="none" stroke={color} strokeWidth={2} />;
  if (symbol === 'x')
    return (
      <g stroke={color} strokeWidth={2}>
        <line x1={x - 5} y1={y - 5} x2={x + 5} y2={y + 5} />
        <line x1={x - 5} y1={y + 5} x2={x + 5} y2={y - 5} />
      </g>
    );
  if (symbol === 'lt')
    return (
      <text x={x - 1} y={y} fontSize={17} textAnchor="middle" dominantBaseline="central" fill={color} fontWeight={700}>
        &lt;
      </text>
    );
  if (symbol === 'gt')
    return (
      <text x={x + 1} y={y} fontSize={17} textAnchor="middle" dominantBaseline="central" fill={color} fontWeight={700}>
        &gt;
      </text>
    );
  if (symbol === 'tri-r') return <polygon points={`${x - 5},${y - 5} ${x - 5},${y + 5} ${x + 6},${y}`} fill={color} />;
  if (symbol === 'tri-l') return <polygon points={`${x + 5},${y - 5} ${x + 5},${y + 5} ${x - 6},${y}`} fill={color} />;
  // Vía aérea enmascarada: triángulo ABIERTO (OD) / cuadrado ABIERTO (OI) —
  // se distinguen de los triángulos RELLENOS de LDL (tri-r/tri-l) a
  // propósito, para no confundir un símbolo con el otro en el mismo
  // gráfico.
  if (symbol === 'triangle-open')
    return <polygon points={`${x},${y - 7} ${x - 6},${y + 5} ${x + 6},${y + 5}`} fill="none" stroke={color} strokeWidth={2} />;
  if (symbol === 'square-open') return <rect x={x - 5} y={y - 5} width={10} height={10} fill="none" stroke={color} strokeWidth={2} />;
  // Vía ósea enmascarada: corchetes, mismo criterio clínico que </> para la
  // vía ósea sin enmascarar.
  if (symbol === 'bracket-r')
    return (
      <text x={x} y={y} fontSize={16} textAnchor="middle" dominantBaseline="central" fill={color} fontWeight={700}>
        [
      </text>
    );
  if (symbol === 'bracket-l')
    return (
      <text x={x} y={y} fontSize={16} textAnchor="middle" dominantBaseline="central" fill={color} fontWeight={700}>
        ]
      </text>
    );
  return null;
}

// `dashed` (22-sep-2026, pedido de Nicolás — bug real): la vía ósea se
// grafica con la misma convención clínica que la vía aérea (unir los
// umbrales con una línea, del mismo color del oído) pero DISCONTINUA para
// distinguirla de la vía aérea a simple vista — antes esta serie ni
// siquiera pasaba `connect`, así que no se dibujaba ninguna línea.
// `symbolFor(freq)` en vez de un `symbol` fijo — cada punto puede tener un
// símbolo distinto (enmascarado o no) sin dejar de compartir la misma línea
// de unión.
//
// `nr` / `nrDir` (migración 0056, 15-oct-2026, pedido de Nicolás): mapa de
// frecuencias "sin respuesta" (sin umbral). Esos puntos llevan el mismo
// símbolo que les correspondería (con o sin enmascaramiento) más una flecha
// hacia abajo — convención clínica, apuntando hacia afuera según el oído
// (`nrDir`: -1 = OD, flecha hacia abajo-izquierda; +1 = OI, hacia
// abajo-derecha). Los puntos SR NO se unen con línea (no hay umbral real que
// conectar): la línea se corta en esa frecuencia y sigue en la siguiente
// con respuesta.
function NrArrow({ x, y, color, dir }) {
  const x1 = x + dir * 4;
  const y1 = y + 9;
  const x2 = x + dir * 12;
  const y2 = y + 21;
  return (
    <g stroke={color} strokeWidth={1.8} fill="none">
      <line x1={x1} y1={y1} x2={x2} y2={y2} />
      <polyline points={`${x2 - dir * 0.5},${y2 - 6} ${x2},${y2} ${x2 - dir * 6},${y2 - 1.5}`} />
    </g>
  );
}

function Series({ data, color, symbolFor, connect, dashed, nr = {}, nrDir = 1 }) {
  const freqs = FREQS.filter((f) => data[f] !== undefined);
  const pts = freqs.map((f) => [xFor(f), yFor(data[f])]);
  if (pts.length === 0) return null;
  // Tramos de línea: se parte en cada frecuencia SR.
  const runs = [];
  let current = [];
  freqs.forEach((f, i) => {
    if (nr[f]) {
      if (current.length) runs.push(current);
      current = [];
    } else {
      current.push(pts[i]);
    }
  });
  if (current.length) runs.push(current);
  return (
    <>
      {connect &&
        runs
          .filter((run) => run.length > 1)
          .map((run, i) => (
            <polyline
              key={i}
              points={run.map((p) => p.join(',')).join(' ')}
              fill="none"
              stroke={color}
              strokeWidth={1.8}
              strokeDasharray={dashed ? '5 4' : undefined}
            />
          ))}
      {pts.map(([x, y], i) => (
        <g key={i}>
          <Marker x={x} y={y} color={color} symbol={symbolFor(freqs[i])} />
          {nr[freqs[i]] && <NrArrow x={x} y={y} color={color} dir={nrDir} />}
        </g>
      ))}
    </>
  );
}

// `odMasked`/`oiMasked`/`boneOdMasked`/`boneOiMasked` (migración 0053) —
// objetos `{ frecuencia: true }`, mismo formato que ya usan od/oi/bone_od/
// bone_oi para los valores en dB. LDL no tiene concepto de enmascaramiento,
// no recibe ningún mapa.
export default function AudiogramSVG({
  od = {},
  oi = {},
  boneOd = {},
  boneOi = {},
  ldlOd = {},
  ldlOi = {},
  odMasked = {},
  oiMasked = {},
  boneOdMasked = {},
  boneOiMasked = {},
  odNr = {},
  oiNr = {},
  boneOdNr = {},
  boneOiNr = {},
}) {
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: 580 }}>
      {DB_LINES.map((db) => {
        const y = yFor(db);
        return (
          <g key={db}>
            <line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke="#DCE3E5" strokeWidth={1} />
            {db % 20 === 0 && (
              <text x={PAD_L - 10} y={y + 4} fontSize={11} textAnchor="end" fill="#5B6B6E" fontFamily={MONO}>
                {db}
              </text>
            )}
          </g>
        );
      })}
      {FREQS.map((f) => {
        const x = xFor(f);
        return (
          <g key={f}>
            <line x1={x} y1={PAD_T} x2={x} y2={H - PAD_B} stroke="#DCE3E5" strokeWidth={1} />
            <text x={x} y={H - PAD_B + 17} fontSize={10.5} textAnchor="middle" fill="#5B6B6E" fontFamily={MONO}>
              {f >= 1000 ? `${f / 1000}k` : f}
            </text>
          </g>
        );
      })}
      <Series data={od} color="#C4453C" symbolFor={(f) => (odMasked[f] ? 'triangle-open' : 'circle')} connect nr={odNr} nrDir={-1} />
      <Series data={oi} color="#2563A6" symbolFor={(f) => (oiMasked[f] ? 'square-open' : 'x')} connect nr={oiNr} nrDir={1} />
      <Series data={boneOd} color="#C4453C" symbolFor={(f) => (boneOdMasked[f] ? 'bracket-r' : 'lt')} connect dashed nr={boneOdNr} nrDir={-1} />
      <Series data={boneOi} color="#2563A6" symbolFor={(f) => (boneOiMasked[f] ? 'bracket-l' : 'gt')} connect dashed nr={boneOiNr} nrDir={1} />
      <Series data={ldlOd} color="#C4453C" symbolFor={() => 'tri-r'} />
      <Series data={ldlOi} color="#2563A6" symbolFor={() => 'tri-l'} />
    </svg>
  );
}
